import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Send, UserPlus, MapPin, Building2, Ticket, Clock, UserRound, Ban, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

function NameAutocomplete({ token, value, onChange, testid }) {
  const [q, setQ] = useState(value || "");
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  useEffect(() => { setQ(value || ""); }, [value]);

  const fetchOpts = (text) => {
    if (timer.current) clearTimeout(timer.current);
    if (!text || text.trim().length < 1) { setOpts([]); return; }
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/public/team/${token}/suggest`, { params: { q: text } });
        setOpts(data || []);
      } catch { setOpts([]); }
    }, 200);
  };

  return (
    <div className="relative">
      <Input value={q} autoComplete="off" placeholder="Nom Prénom" data-testid={testid}
        onChange={(e) => { setQ(e.target.value); onChange(e.target.value); fetchOpts(e.target.value); setOpen(true); }}
        onFocus={() => q && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)} className="h-8 text-xs" />
      {open && opts.length > 0 && (
        <div className="absolute z-[60] mt-1 w-full max-h-52 overflow-auto rounded-md border border-border bg-popover shadow-xl" data-testid={`${testid}-list`}>
          {opts.map((o, i) => (
            <button type="button" key={o} className="w-full text-left px-3 py-1.5 text-sm hover:bg-secondary"
              onMouseDown={(e) => { e.preventDefault(); onChange(o); setQ(o); setOpen(false); }}
              data-testid={`${testid}-option-${i}`}>{o}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CompleterEquipesPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [validating, setValidating] = useState(false);
  const [done, setDone] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [unavailable, setUnavailable] = useState([]);
  const [newUnavail, setNewUnavail] = useState("");
  const personTimers = useRef({});

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/public/team/${token}`);
      setData(d);
      setRows(d.rows.map((r) => ({ ...r, confirm: false, observations: "", obsEditing: true, poste: "", vm: "", pas_dispo: false })));
      setUnavailable((d.unavailable || []).map((n) => ({ name: n })));
    } catch (e) { setErr(apiError(e.response?.data?.detail)); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const updateRow = (idx, patch) => setRows((list) => list.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const setName = (idx, v) => updateRow(idx, { nom_prenom: v });
  const fetchPerson = (idx, name) => {
    if (personTimers.current[idx]) clearTimeout(personTimers.current[idx]);
    if (!name || !name.trim()) { updateRow(idx, { poste: "", vm: "" }); return; }
    personTimers.current[idx] = setTimeout(async () => {
      try { const { data: p } = await api.get(`/public/team/${token}/person`, { params: { name } }); updateRow(idx, { poste: p.poste || "", vm: p.vm || "" }); } catch { /* ignore */ }
    }, 400);
  };
  const onName = (idx, v) => { setName(idx, v); fetchPerson(idx, v); };
  const markUnavailable = (idx) => {
    const nm = (rows[idx]?.nom_prenom || "").trim();
    if (nm) setUnavailable((u) => (u.some((x) => x.name.toLowerCase() === nm.toLowerCase()) ? u : [...u, { name: nm }]));
    updateRow(idx, { nom_prenom: "", pas_dispo: true, poste: "", vm: "" });
    if (nm) toast.success(`${nm} déplacé dans « Pas dispo »`);
  };
  const addUnavailable = () => {
    const nm = newUnavail.trim();
    if (!nm) return;
    setUnavailable((u) => (u.some((x) => x.name.toLowerCase() === nm.toLowerCase()) ? u : [...u, { name: nm }]));
    setNewUnavail("");
  };
  const removeUnavailable = (i) => setUnavailable((u) => u.filter((_, k) => k !== i));

  const validate = async () => {
    setValidating(true);
    try {
      const { data: res } = await api.post(`/public/team/${token}/validate`, { rows, unavailable: unavailable.map((u) => u.name) });
      toast.success(`Équipe validée — ${res.count} personne(s), ${res.regisseurs_notified} régisseur(s) notifié(s)`);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setValidating(false); }
  };

  const invite = async () => {
    if (!inviteEmail.trim()) { toast.error("Email requis"); return; }
    setInviting(true);
    try {
      await api.post(`/public/team/${token}/invite`, { email: inviteEmail.trim() });
      toast.success("Demande d'inscription envoyée");
      setInviteEmail("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setInviting(false); }
  };

  if (err) return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
      <div className="text-center"><h1 className="font-display text-2xl font-bold mb-2">Lien indisponible</h1><p className="text-muted-foreground">{err}</p></div>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  const c = data.concert;
  const Info = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-2"><Icon className="h-4 w-4 text-primary mt-0.5 shrink-0" />
      <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div><div className="text-sm font-medium">{value || "—"}</div></div></div>
  );
  const VmBadge = ({ vm }) => vm === "a_jour"
    ? <span className="text-emerald-400 font-semibold text-xs whitespace-nowrap">À jour</span>
    : vm === "pas_a_jour"
    ? <span className="text-red-400 font-semibold text-xs whitespace-nowrap">Pas à jour</span>
    : <span className="text-muted-foreground text-xs">—</span>;

  const rubriqueOf = (r) => {
    if (r.fonction === "Aide Catering") return "catering";
    if (data.is_ouvreuse) return "ouvreuses";
    if (r.fonction === "Régisseur/se Général(e)" || r.fonction === "Attaché(e) de Production" || r.specifications === "Chef(fe) d'équipe") return "productions";
    if (r.fonction === "Cariste") return "caristes";
    return "plateau";
  };
  const RUBRIQUE_ORDER = data.is_ouvreuse ? ["ouvreuses"] : ["productions", "caristes", "plateau", "catering"];
  const RUBRIQUE_LABEL = { productions: "Productions", caristes: "Caristes", plateau: "Techs Plateau & Spécialisés", catering: "Aides-Catering", ouvreuses: "Ouvreuses" };
  const nCols = data.is_ouvreuse ? 9 : 12;
  const cntBy = (pred) => rows.filter(pred).length;
  const cnt = {
    prod: cntBy((r) => rubriqueOf(r) === "productions"),
    regisseurs: cntBy((r) => r.fonction === "Régisseur/se Général(e)"),
    chefs: cntBy((r) => r.specifications === "Chef(fe) d'équipe" && r.fonction !== "Aide Catering"),
    attaches: cntBy((r) => r.fonction === "Attaché(e) de Production"),
    caristes: cntBy((r) => r.fonction === "Cariste" && !data.is_ouvreuse),
    plateauSpe: cntBy((r) => rubriqueOf(r) === "plateau"),
    habilleuse: cntBy((r) => r.specifications === "Habilleuse"),
    son: cntBy((r) => r.specifications === "Son"),
    lumiere: cntBy((r) => r.specifications === "Lights"),
    poursuiteur: cntBy((r) => r.specifications === "Poursuiteur/se"),
    video: cntBy((r) => r.specifications === "Vidéo"),
    plateau: cntBy((r) => r.fonction === "Technicien(ne) de Plateau"),
    permanent: cntBy((r) => r.specifications === "Permanent"),
    scaff: cntBy((r) => r.specifications === "Scaff"),
    catering: cntBy((r) => r.fonction === "Aide Catering"),
    ouvreuses: cntBy(() => data.is_ouvreuse),
  };
  const totalDemande = rows.length;
  const renderStat = (label, value, head = false) => (
    <div className={`flex justify-between ${head ? "border-t border-border/50 pt-2 mt-1" : "pl-3"}`}>
      <span className={head ? "font-semibold" : "text-muted-foreground"}>{label}</span>
      <span className={`tabular-nums ${head ? "font-bold text-primary" : "font-medium"}`}>{value}</span>
    </div>
  );
  const orderedRows = rows.map((r, i) => ({ r, i })).sort((a, b) => RUBRIQUE_ORDER.indexOf(rubriqueOf(a.r)) - RUBRIQUE_ORDER.indexOf(rubriqueOf(b.r)));

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card">
        <div className="mx-auto max-w-[1400px] px-6 py-4 flex items-center gap-3">
          <img src="/logo.svg" alt="Les Derniers Couchés" className="h-9 w-9" />
          <div className="leading-tight">
            <div className="font-display font-bold text-sm tracking-tight">LES DERNIERS COUCHÉS</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-primary/80">Compléter les équipes · {data.role_label}</div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 sm:px-6 py-8 space-y-6">
        {done ? (
          <div className="rounded-xl border border-border bg-card p-10 text-center" data-testid="team-validated">
            <CheckCircle2 className="h-14 w-14 text-primary mx-auto mb-4" />
            <h1 className="font-display text-2xl font-extrabold mb-2">Équipe validée, merci !</h1>
            <p className="text-muted-foreground">Les noms ont été enregistrés dans la synthèse et les régisseurs affectés ont été notifiés.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-6" data-testid="team-recap">
                <div className="text-xs uppercase tracking-[0.2em] text-primary">{c.date_label}</div>
                <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mt-1">{c.concert}</h1>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-5">
                  <Info icon={Clock} label="Heure" value={c.heure} />
                  <Info icon={Building2} label="Salle" value={c.salle} />
                  <Info icon={MapPin} label="Ville" value={c.ville} />
                  <Info icon={Ticket} label="Config" value={c.config} />
                  <Info icon={Ticket} label="Jauge" value={c.jauge} />
                  <Info icon={Building2} label="Prod" value={c.prod} />
                  <Info icon={UserRound} label="Régisseur(s)" value={(c.regisseurs || []).join(", ")} />
                </div>
              </div>

              <div className="lg:col-span-1 rounded-2xl border border-border bg-card p-6" data-testid="team-totals-card">
                <h2 className="font-display text-lg font-bold">Récap effectifs</h2>
                <div className="mt-3 flex items-baseline justify-between border-b border-border pb-3">
                  <span className="text-sm text-muted-foreground">Total personnes demandées</span>
                  <span className="font-display text-2xl font-extrabold text-primary" data-testid="total-demande">{totalDemande}</span>
                </div>
                <div className="mt-3 space-y-1.5 text-sm">
                  {data.is_ouvreuse ? renderStat("Ouvreuses", cnt.ouvreuses, true) : (
                    <>
                      {renderStat("Production", cnt.prod, true)}
                      {renderStat("Régisseur(s)", cnt.regisseurs)}
                      {renderStat("Chef(fe) d'équipe", cnt.chefs)}
                      {renderStat("Attaché.e(s) de Production", cnt.attaches)}
                      {renderStat("Caristes", cnt.caristes, true)}
                      {renderStat("Techs Plateau & Spécialisés", cnt.plateauSpe, true)}
                      {renderStat("Habilleuse", cnt.habilleuse)}
                      {renderStat("Techs son", cnt.son)}
                      {renderStat("Techs lumière", cnt.lumiere)}
                      {renderStat("Techs poursuiteur", cnt.poursuiteur)}
                      {renderStat("Techs vidéo", cnt.video)}
                      {renderStat("Techs plateau (toutes vac.)", cnt.plateau)}
                      {renderStat("Techs permanent", cnt.permanent)}
                      {renderStat("Scaff", cnt.scaff)}
                      {renderStat("Aides-Catering", cnt.catering, true)}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-card p-5">
              <h2 className="font-display text-lg font-bold mb-1">Besoins en personnel</h2>
              <p className="text-xs text-muted-foreground mb-4">Renseignez le nom de chaque {data.is_ouvreuse ? "hôte/hôtesse" : "intermittent"} (suggestions depuis la base).</p>
              {rows.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucun besoin {data.is_ouvreuse ? "ouvreuses" : "techs"} saisi pour ce concert.</p>
              ) : (
                <div className="rounded-xl border border-border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="text-sm border-collapse min-w-full" data-testid="team-rows-table">
                      <thead><tr className="bg-secondary/60 text-muted-foreground text-xs">
                        <th className="px-2 py-2 text-left">#</th>
                        <th className="px-2 py-2 text-left">Fonction</th>
                        {!data.is_ouvreuse && <th className="px-2 py-2 text-left">Spéc.</th>}
                        <th className="px-2 py-2 text-left">Vacation</th>
                        <th className="px-2 py-2 text-left">Get in</th>
                        <th className="px-2 py-2 text-left">Nbre H</th>
                        {!data.is_ouvreuse && <th className="px-2 py-2 text-left">Poste</th>}
                        <th className="px-2 py-2 text-left min-w-[190px]">Nom Prénom</th>
                        {!data.is_ouvreuse && <th className="px-2 py-2 text-left">VM</th>}
                        <th className="px-2 py-2 text-left min-w-[210px]">Observations</th>
                        <th className="px-2 py-2 text-center">Confirm</th>
                        <th className="px-2 py-2 text-center">Dispo</th>
                      </tr></thead>
                      <tbody className="divide-y divide-border">
                        {(() => {
                          const out = [];
                          let seen = null;
                          orderedRows.forEach(({ r, i: idx }, di) => {
                            const rub = rubriqueOf(r);
                            if (rub !== seen) {
                              seen = rub;
                              out.push(
                                <tr key={`h-${rub}`} className="bg-primary/10"><td colSpan={nCols} className="px-3 py-1.5 font-semibold text-primary text-xs uppercase tracking-wider" data-testid={`rubrique-${rub}`}>{RUBRIQUE_LABEL[rub]}</td></tr>
                              );
                            }
                            const posteVm = !data.is_ouvreuse && r.fonction !== "Aide Catering";
                            out.push(
                              <tr key={idx} className={`hover:bg-secondary/20 ${r.pas_dispo ? "opacity-50" : ""}`} data-testid={`team-row-${idx}`}>
                                <td className="px-2 py-1.5 text-xs text-muted-foreground">{di + 1}</td>
                                <td className="px-2 py-1.5 text-xs whitespace-nowrap">{r.fonction || "—"}</td>
                                {!data.is_ouvreuse && <td className="px-2 py-1.5 text-xs whitespace-nowrap">{r.specifications || "—"}</td>}
                                <td className="px-2 py-1.5 text-xs whitespace-nowrap">{r.vacation || "—"}</td>
                                <td className="px-2 py-1.5 text-xs whitespace-nowrap">{r.heure_debut || "—"}</td>
                                <td className="px-2 py-1.5 text-xs whitespace-nowrap">{r.nbre_h || "—"}</td>
                                {!data.is_ouvreuse && <td className="px-2 py-1.5 text-xs whitespace-nowrap" data-testid={`team-poste-${idx}`}>{posteVm ? (r.poste || "—") : "—"}</td>}
                                <td className="px-2 py-1.5">
                                  <NameAutocomplete token={token} value={r.nom_prenom} onChange={(v) => onName(idx, v)} testid={`team-name-${idx}`} />
                                </td>
                                {!data.is_ouvreuse && <td className="px-2 py-1.5" data-testid={`team-vm-${idx}`}>{posteVm ? <VmBadge vm={r.vm} /> : <span className="text-muted-foreground text-xs">—</span>}</td>}
                                <td className="px-2 py-1.5">
                                  {r.obsEditing !== false ? (
                                    <div className="flex items-center gap-1">
                                      <Input value={r.observations || ""} onChange={(e) => updateRow(idx, { observations: e.target.value })} placeholder="Observations" className="h-8 text-xs" data-testid={`team-obs-${idx}`} />
                                      <Button size="sm" variant="outline" className="h-8 px-2 text-xs" onClick={() => updateRow(idx, { obsEditing: false })} data-testid={`team-obs-save-${idx}`}>Enreg.</Button>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs">{r.observations || "—"}</span>
                                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => updateRow(idx, { obsEditing: true })} data-testid={`team-obs-edit-${idx}`}>Modifier</Button>
                                    </div>
                                  )}
                                </td>
                                <td className="px-2 py-1.5 text-center">
                                  <Checkbox checked={!!r.confirm} onCheckedChange={(v) => updateRow(idx, { confirm: !!v })} data-testid={`team-confirm-${idx}`} />
                                </td>
                                <td className="px-2 py-1.5 text-center">
                                  <Button size="sm" variant="outline" className="h-8 px-2 text-xs" onClick={() => markUnavailable(idx)} data-testid={`team-pasdispo-${idx}`}><Ban className="h-3.5 w-3.5 mr-1" />Pas dispo</Button>
                                </td>
                              </tr>
                            );
                          });
                          return out;
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {rows.length > 0 && (
                <div className="mt-4 flex justify-end">
                  <Button onClick={validate} disabled={validating} className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-11 px-6" data-testid="validate-team-button">
                    <CheckCircle2 className="h-4 w-4 mr-2" /> {validating ? "Validation..." : "Valider l'équipe"}
                  </Button>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-border bg-card p-5" data-testid="pasdispo-section">
              <h2 className="font-display text-lg font-bold mb-1">Pas dispo</h2>
              <p className="text-xs text-muted-foreground mb-4">Personnes indisponibles pour ce concert (déclarées via le bouton « Pas dispo » ci-dessus, ou ajoutées ici).</p>
              <div className="flex items-center gap-2 max-w-md mb-4">
                <div className="flex-1"><NameAutocomplete token={token} value={newUnavail} onChange={setNewUnavail} testid="pasdispo-name" /></div>
                <Button variant="outline" onClick={addUnavailable} data-testid="pasdispo-add"><UserPlus className="h-4 w-4 mr-1.5" /> Ajouter</Button>
              </div>
              {unavailable.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucune personne indisponible.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {unavailable.map((u, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-red-500/40 bg-red-500/10 px-3 py-1 text-xs text-red-300" data-testid={`pasdispo-item-${i}`}>
                      {u.name}
                      <button onClick={() => removeUnavailable(i)} className="hover:text-red-100" data-testid={`pasdispo-remove-${i}`}><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-border bg-card p-5">
              <h2 className="font-display text-base font-bold mb-1">Personne non inscrite ?</h2>
              <p className="text-xs text-muted-foreground mb-3">Envoyez-lui un lien pour compléter sa fiche d'inscription.</p>
              <div className="flex items-center gap-2 max-w-md">
                <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} type="email" placeholder="email@exemple.fr" data-testid="invite-email" />
                <Button variant="outline" onClick={invite} disabled={inviting} data-testid="send-invite-button">
                  <UserPlus className="h-4 w-4 mr-1.5" /> {inviting ? "..." : "Envoyer"}
                </Button>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
